import { apiClient } from '@/lib/api-client'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

// Raw API response interface - matches backend PlanSchema
interface PlanApiResponse {
  id: string
  tenant_id: string
  environment_id: string
  name: string
  code: string
  description: string | null
  target_audience: string
  currency: string
  category: string
  plan_type: string
  billing_period: 'monthly' | 'quarterly' | 'yearly'
  trial_available: boolean
  trial_days: number
  status: 'active' | 'draft' | 'archived'
  version: number
  parent_plan_id: string | null
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
  charges: Record<string, unknown>[]
  agent_actions: Record<string, unknown>[]
  features: Record<string, unknown>[]
}

// Processed interface for use in the app
export interface Plan {
  id: string
  tenant_id: string
  environment_id: string
  name: string
  code: string
  description: string | null
  target_audience: string
  currency: string
  category: string
  plan_type: string
  billing_period: 'monthly' | 'quarterly' | 'yearly'
  trial_available: boolean
  trial_days: number
  status: 'active' | 'draft' | 'archived'
  version: number
  parent_plan_id: string | null
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
  charges: Record<string, unknown>[]
  agent_actions: Record<string, unknown>[]
  features: Record<string, unknown>[]
  // Backward compatibility property for components expecting billing_interval
  billing_interval: 'monthly' | 'quarterly' | 'yearly'
}

class PlansService {
  private mapPlanResponse(plan: PlanApiResponse): Plan {
    if (!plan) {
      throw new Error('Plan data is undefined')
    }
    return {
      ...plan,
      billing_interval: plan.billing_period || 'monthly',
    }
  }

  async getPlans(
    category: 'plan' | 'addon' = 'plan',
    options?: { status?: 'active' | 'draft' | 'archived' }
  ): Promise<Plan[]> {
    const params: Record<string, string> = { category }

    // Add optional status filter if provided
    if (options?.status) {
      params.status = options.status
    }

    const response = await apiClient.get<{ data: PlanApiResponse[] }>(
      '/plans',
      {
        params,
      }
    )
    return response.data.data.map(plan => this.mapPlanResponse(plan))
  }

  async getPlanById(id: string): Promise<Plan> {
    try {
      const response = await apiClient.get<{ data: PlanApiResponse }>(
        `/plans/${id}`
      )
      // Check if response has the nested data structure
      const planData = response.data.data || response.data
      return this.mapPlanResponse(planData)
    } catch (error) {
      toast.error(extractErrorMessage(error))
      // Fallback: try to find the plan in both plan and addon lists
      try {
        const plans = await this.getPlans('plan')
        const foundPlan = plans.find(plan => plan.id === id)
        if (foundPlan) {
          return foundPlan
        }

        // If not found in plans, try addons
        const addons = await this.getPlans('addon')
        const foundAddon = addons.find(addon => addon.id === id)
        if (foundAddon) {
          return foundAddon
        }

        throw new Error(`Plan with ID ${id} not found`)
      } catch (fallbackError) {
        toast.error(extractErrorMessage(fallbackError))
        throw new Error(`Plan with ID ${id} not found`)
      }
    }
  }
}

export const plansService = new PlansService()
